import React, { useMemo } from 'react';
import { CheckCircle, AlertTriangle, Layers, Zap, BarChart3 } from 'lucide-react';
import { validateSeedCoverage, validateSeedQuestionsLocal, getAllSeedQuestions, getSeedQuestionTypeDistribution, SEED_COLLECTIONS, getSeedCollection, TAXONOMY, FAMILIES_BY_MODULE, MODULES, type ModuleId } from '../../../../content';
import { QUESTION_TYPE_META } from '../../../../content/questions/questionTypeRegistry';
import type { QuestionType } from '../../../../content/types';
import { getModuleHexColor } from '../../../../content/moduleColors';
export function SeedStatus() {
  const coverageReport = useMemo(() => validateSeedCoverage(), []);
  const validationReport = useMemo(() => validateSeedQuestionsLocal(), []);
  const typeDistribution = useMemo(() => getSeedQuestionTypeDistribution(), []);
  const coveragePercent = Math.round(coverageReport.coveredFamilies / coverageReport.totalFamilies * 100);
  const sortedTypes = useMemo(() => {
    return Object.entries(typeDistribution).sort((a, b) => b[1] - a[1]);
  }, [typeDistribution]);
  return <div className="space-y-6 sm:space-y-8" data-id="element-8288" data-mp="1ivv0zc8keayn">
      {/* 1. Module Overzicht */}
      <div data-id="element-8289" data-mp="1nq92lwdtlebr">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8290" data-mp="1nqsht5c35om3">
          <BarChart3 className="w-5 h-5 text-blue-600" data-id="element-8291" data-mp="19z47n7leda7r" />
          Module Overzicht
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-8292" data-mp="1ewvvnqix8c6o">
          {MODULES.map(module => {
          const collection = SEED_COLLECTIONS.get(module.id);
          const families = FAMILIES_BY_MODULE.get(module.id) || [];
          const questionCount = collection?.questions.length || 0;
          const familyCount = families.length;
          const moduleFamilyIds = families.map(f => f.familyId);
          const coveredModuleFamilies = new Set(collection?.questions.map(q => q.familyId) || []);
          const validCoveredCount = moduleFamilyIds.filter(id => coveredModuleFamilies.has(id)).length;
          const coverage = familyCount > 0 ? Math.round(validCoveredCount / familyCount * 100) : 0;
          const tiers = collection?.questions.map(q => q.tier).filter(Boolean) || [];
          const minTier = tiers.length > 0 ? Math.min(...tiers) : 0;
          const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
          return <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{
            borderLeftWidth: '4px',
            borderLeftColor: getModuleColor(module.id)
          }} data-id="element-8293" data-mp="3kk3fv1fr53pw">
                
                <div className="p-4 flex-1" data-id="element-8294" data-mp="3ani6k3wp96g9">
                  <div className="flex justify-between items-start mb-4" data-id="element-8295" data-mp="36j0smld0tnde">
                    <h4 className="font-bold text-gray-900" data-id="element-8296" data-mp="31kz77ts4hbyr">{module.title}</h4>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md" data-id="element-8297" data-mp="31n27d6mzolh7">
                      {collection?.moduleCode || module.id.substring(0, 3).toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-2" data-id="element-8298" data-mp="17bu3d1914udq">
                    <div className="flex justify-between text-sm" data-id="element-8299" data-mp="17b14emgbb965">
                      <span className="text-gray-500" data-id="element-8300" data-mp="1canewo4rabb9">Vragen</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-8301" data-mp="1h2598tc9xdn9">
                        {questionCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-8302" data-mp="1m3kvupu3ns7r">
                      <span className="text-gray-500" data-id="element-8303" data-mp="ofkj1w7jb7la">Families</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-8304" data-mp="tcaxyg7lrx54">
                        {familyCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-8305" data-mp="12dsh67gb4ey1">
                      <span className="text-gray-500" data-id="element-8306" data-mp="12f3ouicvi6uf">Tier bereik</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-8307" data-mp="29qkjl2kgn7s9">
                        {minTier > 0 ? `${minTier}-${maxTier}` : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100" data-id="element-8308" data-mp="2cdlhpthw3xff">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-8309" data-mp="27eifshx7i7bo">
                    <span className="text-gray-500 font-medium" data-id="element-8310" data-mp="2mapx9ah8cun9">Dekking</span>
                    <span className={`font-bold ${coverage === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-8311" data-mp="2halpc8ue188n">
                      
                      {coverage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-8312" data-mp="2qarzbkqvnq32">
                    <div className={`h-1.5 rounded-full ${coverage === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                  width: `${coverage}%`
                }} data-id="element-8313" data-mp="350z796j9dqpi" />
                    
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-8314" data-mp="3a0bu3ra4qxs3">
        {/* 2. Dekkingsstatus */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-8315" data-mp="3snb5tniroxls">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-8316" data-mp="1hrw2q0bn8ex">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-8317" data-mp="3jno5l51rxxub">
              <CheckCircle className="w-4 h-4 text-emerald-600" data-id="element-8318" data-mp="3olzhdlbmqif6" />
              Dekkingsstatus
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-8319" data-mp="39wacpg4bvwa5">
            <div className="mb-6" data-id="element-8320" data-mp="39v8n86mkf78w">
              <div className="flex justify-between items-end mb-2" data-id="element-8321" data-mp="3ens4peodimiz">
                <span className="text-sm font-medium text-gray-600" data-id="element-8322" data-mp="3eq4pdzlinazq">
                  Totale dekking
                </span>
                <span className={`text-2xl font-bold ${coveragePercent === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-8323" data-mp="34xz1b0ydxdpt">
                  
                  {coveragePercent}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-8324" data-mp="2vxtvgv8uandr">
                <div className={`h-full transition-all duration-500 ${coveragePercent === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                width: `${coveragePercent}%`
              }} data-id="element-8325" data-mp="11hvgsahwzlsu" />
                
              </div>
            </div>

            {coverageReport.missingFamilies.length > 0 ? <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-8326" data-mp="nqgu9tost2h1">
                <h4 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-8327" data-mp="1kfxxm20b6cfk">
                  <AlertTriangle className="w-4 h-4" data-id="element-8328" data-mp="1gevx7hunnuc0" />
                  Ontbrekende families ({coverageReport.missingFamilies.length})
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-8329" data-mp="1bdyk3joi5qhd">
                  {coverageReport.missingFamilies.map(id => <div key={id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-100 text-sm" data-id="element-8330" data-mp="16ij4onaujmiu">
                  
                      <span className="font-mono text-amber-900" data-id="element-8331" data-mp="3r5mcr2whgwud">{id}</span>
                    </div>)}
                </div>
              </div> : <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-id="element-8332" data-mp="3h4k0tm7bfx04">
                <div className="bg-emerald-100 p-2 rounded-full flex-shrink-0" data-id="element-8333" data-mp="3lzpmql0pxbo7">
                  <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-8334" data-mp="3qzc0qwodevy2" />
                </div>
                <div data-id="element-8335" data-mp="3vserkx1rjrak">
                  <div className="font-bold text-emerald-900" data-id="element-8336" data-mp="3vtpxb2err94b">
                    Alle families gedekt
                  </div>
                  <div className="text-sm text-emerald-700" data-id="element-8337" data-mp="8mnkjvm15e15">
                    Alle {coverageReport.totalFamilies} families uit de
                    taxonomie hebben minimaal één seed vraag.
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* 3. Vraagtypen */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-8338" data-mp="2fdwvsqk63pfx">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-8339" data-mp="2kbybd1idx6mn">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-8340" data-mp="q4cm7v5aus1a">
              <Layers className="w-4 h-4 text-purple-600" data-id="element-8341" data-mp="l5j7jzea0uzj" />
              Vraagtypen
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-8342" data-mp="zu5uam68gp0s">
            <div className="space-y-3" data-id="element-8343" data-mp="zt4clyupp4a3">
              {sortedTypes.map(([type, count]) => <div key={type} className="flex items-center justify-between" data-id="element-8344" data-mp="uv2r779tcsvg">
                  <div className="flex items-center gap-2" data-id="element-8345" data-mp="75i9sstb63oa">
                    <CheckCircle className="w-4 h-4 text-emerald-500" data-id="element-8346" data-mp="77ulwpcdxjtf" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-8347" data-mp="27ysfk1bdas4">
                      {translateQuestionType(type)}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-id="element-8348" data-mp="c4bj091kb8in">
                    {count}
                  </span>
                </div>)}
              {sortedTypes.length === 0 && <div className="text-sm text-gray-500 text-center py-4" data-id="element-8349" data-mp="1niyl648006oj">
                  Geen vraagtypen gevonden
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* 4. Validatie */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-8350" data-mp="2046zvcvt1cd6">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center" data-id="element-8351" data-mp="1l6mw29an9n8l">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-8352" data-mp="1q4ofwwfw9ql4">
            <Zap className="w-4 h-4 text-amber-500" data-id="element-8353" data-mp="2duiubifv3n7h" />
            Validatie Resultaten
          </h3>
          <div className="flex gap-2 text-xs font-medium" data-id="element-8354" data-mp="2iskcm15grihw">
            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full" data-id="element-8355" data-mp="244qwmfcgudys">
              {validationReport.valid} Geldig
            </span>
            {validationReport.invalid > 0 && <span className="px-2.5 py-1 bg-rose-100 text-rose-700 rounded-full" data-id="element-8356" data-mp="2wt4vexbdpebe">
                {validationReport.invalid} Ongeldig
              </span>}
          </div>
        </div>
        <div className="p-4 sm:p-5" data-id="element-8357" data-mp="31lfrfp300fyo">
          {validationReport.issues.length > 0 ? <div className="space-y-3" data-id="element-8358" data-mp="2i194dxq67p06">
              {validationReport.issues.map(issue => <div key={issue.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 sm:p-4" data-id="element-8359" data-mp="2m0rst3h7ai7r">
              
                  <div className="font-mono text-sm font-bold text-rose-800 mb-2" data-id="element-8360" data-mp="2821vpnudsije">
                    {issue.id}
                  </div>
                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-8361" data-mp="1ycqn02bepfw1">
                    {issue.problems.map((p, i) => <li key={i} data-id="element-8362" data-mp="233gqfa09v8o3" data-mpi={i}>{p}</li>)}
                  </ul>
                </div>)}
            </div> : <div className="text-center py-8" data-id="element-8363" data-mp="1pclfls67qhhv">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-3" data-id="element-8364" data-mp="1pdn0eod16ics" />
              <p className="text-gray-600 font-medium" data-id="element-8365" data-mp="1ubojh9agiho9">
                Alle seed vragen zijn succesvol gevalideerd
              </p>
            </div>}
        </div>
      </div>
    </div>;
}
// Helpers
function getModuleColor(moduleId: ModuleId): string {
  const mod = MODULES.find(m => m.id === moduleId);
  if (!mod) return '#64748b';
  return getModuleHexColor(mod.color);
}
// F-A4 (19 May 2026): use QUESTION_TYPE_META as the SSOT for Dutch labels
// instead of a hand-maintained local map. The old map covered only 14 of
// the 38 registered types — anything outside that set rendered raw English
// keys (e.g. `text_highlight`, `verb_conjugation_flow`) and drifted out of
// sync as new types were added. Audit §19.2.4 / §20.1.
function translateQuestionType(type: string): string {
  const meta = QUESTION_TYPE_META[type as QuestionType];
  return meta?.label ?? type;
}